import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AccessRecord, useUpdateTeenAccess } from "@/lib/api/teen-access";
import { cn } from "@/lib/utils";
import {
  FlagIcon,
  StarIcon,
  UserIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { useToast } from "../ui/use-toast";

interface EditPermissionsPopoverProps {
  record: AccessRecord;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  anchorPosition?: { x: number; y: number };
  menteeId: string;
}

interface PermissionsFormData {
  viewMentor: boolean;
  viewMonthlyUpdates: boolean;
  viewSelfAssessmentData: boolean;
}

export function EditPermissionsPopover({
  record,
  open,
  onOpenChange,
  anchorPosition,
  menteeId,
}: EditPermissionsPopoverProps) {
  const { toast } = useToast();
  const { mutateAsync: updatePermissions, isPending } =
    useUpdateTeenAccess(menteeId);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<PermissionsFormData>({
    defaultValues: record.permissions,
  });

  const popoverRef = useRef<HTMLDivElement>(null);

  // Reset form when popover opens with record's permissions
  useEffect(() => {
    if (open) {
      reset(record.permissions);
    }
  }, [open, record.permissions, reset]);

  useEffect(() => {
    if (!open || !anchorPosition || !popoverRef.current) return;

    const popover = popoverRef.current;
    const rect = popover.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate position
    let left = anchorPosition.x;
    let top = anchorPosition.y;

    // Adjust horizontal position if it would overflow
    if (left + rect.width > viewportWidth) {
      left = viewportWidth - rect.width - 16;
    }

    // Adjust vertical position if it would overflow
    if (top + rect.height > viewportHeight) {
      top = viewportHeight - rect.height - 16;
    }

    // Apply position
    popover.style.left = `${left}px`;
    popover.style.top = `${top}px`;
  }, [open, anchorPosition]);

  const onSubmit = async (data: PermissionsFormData) => {
    try {
      await updatePermissions({
        userId: record.granted_to_user.id,
        permissions: data,
      });
      onOpenChange?.(false);

      toast({
        title: "Successfully updated permissions",
      });
    } catch (error) {
      console.error(error);

      toast({
        title: "Failed to update permissions",
      });
    }
  };

  const handleClose = () => {
    reset(record.permissions);
    onOpenChange?.(false);
  };

  const permissionItems = [
    {
      id: "viewMentor",
      label: "View your Mentor",
      icon: UserIcon,
    },
    {
      id: "viewMonthlyUpdates",
      label: "View Monthly Updates",
      icon: StarIcon,
    },
    {
      id: "viewSelfAssessmentData",
      label: "View Self-Assessment Data",
      icon: FlagIcon,
    },
  ] as const;

  if (!open) return null;

  return (
    <div
      ref={popoverRef}
      className="fixed z-50 w-[400px] overflow-hidden rounded-[20px] border border-[#EFEFEF] bg-white p-6 shadow-[0px_2px_16px_0px_rgba(0,0,0,0.08)]"
    >
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-[20px] font-bold leading-tight text-black">
              {record.granted_to_user.name}
            </h3>
            <p className="font-twk mt-1 text-base font-normal leading-tight text-[#ABABAB]">
              {record.granted_to_user.phone}
            </p>
            <div className="mt-2">
              {record.types.map((type) => (
                <span
                  key={type}
                  className="mr-2 inline-flex items-center rounded-full border border-[#E8E8E8] bg-[#F9F9F9] px-3 py-1 text-center text-xs font-normal leading-none text-[#777777]"
                >
                  {type}
                </span>
              ))}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="rounded-full p-1 hover:bg-gray-100"
          >
            <XMarkIcon className="h-5 w-5 text-[#ABABAB]" />
          </button>
        </div>

        <div className="space-y-4">
          <h4 className="text-base font-bold text-black">Access</h4>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            {permissionItems.map(({ id, label, icon: Icon }) => (
              <Controller
                key={id}
                control={control}
                name={id as keyof PermissionsFormData}
                render={({ field: { value, onChange } }) => (
                  <div className="flex items-center justify-between transition-colors">
                    <div className="flex items-center space-x-3">
                      <Icon
                        className={cn(
                          "h-5 w-5 transition-colors",
                          value ? "text-[#2563EB]" : "text-[#ABABAB]",
                        )}
                      />
                      <Label
                        htmlFor={id}
                        className={cn(
                          "cursor-pointer text-sm font-medium transition-colors",
                          value ? "text-[#2563EB]" : "text-[#777777]",
                        )}
                      >
                        {label}
                      </Label>
                    </div>
                    <Switch
                      id={id}
                      checked={value}
                      onCheckedChange={onChange}
                      className={cn(
                        "data-[state=checked]:bg-[#2563EB]",
                        "h-6 w-11",
                        "[&>span]:h-5 [&>span]:w-5",
                      )}
                    />
                  </div>
                )}
              />
            ))}

            <div className="flex justify-end space-x-3 pt-3">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={!isDirty || isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
